import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FileAudio, Upload, X, Globe } from 'lucide-react';
import { FolderSelector } from '@/components/FolderSelector';
const LANGUAGE_OPTIONS = [
  { value: 'auto', label: 'Auto-detect' },
  { value: 'en', label: 'English' },
  { value: 'de', label: 'German' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'it', label: 'Italian' },
];

interface UrlPreview {
  title: string;
  author: string;
  duration_minutes?: number;
}

const ACCEPTED_TYPES = ['.mp3', '.mp4', '.m4a', '.wav', '.webm'];
const ACCEPTED_MIME = ['audio/mpeg', 'audio/mp4', 'audio/x-m4a', 'audio/wav', 'audio/webm', 'video/mp4', 'video/webm'];
const MAX_FILE_SIZE = 1024 * 1024 * 1024; // 1GB

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileKey(file: File): string {
  return `${file.name}|${file.size}|${file.lastModified}`;
}

interface HeroInputProps {
  onSubmit: (urls: string[], folderId?: string | null, language?: string) => void;
  collapsed: boolean;
  children?: React.ReactNode;
  onUrlBlur?: (url: string) => void;
  previews?: Record<string, UrlPreview>;
  balance?: number | null;
  inlineError?: React.ReactNode;
  onFilesSelected?: (files: File[], folderId?: string | null, language?: string) => void;
  isLoggedIn?: boolean;
  onPurchaseMore?: () => void;
}

export function HeroInput({ onSubmit, collapsed, children, onUrlBlur, previews, balance, inlineError, onFilesSelected, isLoggedIn, onPurchaseMore }: HeroInputProps) {
  const [urls, setUrls] = useState<string[]>(['']);
  const [error, setError] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [fileError, setFileError] = useState('');
  const [fileDurations, setFileDurations] = useState<Record<string, number>>({});
  const [selectedLanguage, setSelectedLanguage] = useState('auto');
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const objectUrlsRef = useRef<string[]>([]);
  
  // Probe file durations using browser Audio/Video API
  useEffect(() => {
    const urls: string[] = [];

    for (const file of selectedFiles) {
      const key = fileKey(file);
      if (fileDurations[key] !== undefined) continue;

      const url = URL.createObjectURL(file);
      urls.push(url);

      const el = new Audio();
      el.preload = 'metadata';
      el.onloadedmetadata = () => {
        if (Number.isFinite(el.duration)) {
          setFileDurations((prev) => ({ ...prev, [key]: el.duration }));
        }
        URL.revokeObjectURL(url);
      };
      el.onerror = () => {
        URL.revokeObjectURL(url);
      };
      el.src = url;
    }

    objectUrlsRef.current = urls;

    return () => {
      // Clean up any URLs that didn't finish loading
      for (const url of urls) {
        URL.revokeObjectURL(url);
      }
    };
  }, [selectedFiles]);

  const validateUrl = (inputUrl: string): boolean => {
    try {
      const urlObj = new URL(inputUrl);
      return ['http:', 'https:'].includes(urlObj.protocol);
    } catch {
      return false;
    }
  };

  const handleSubmit = () => {
    const nonEmpty = urls.map((u) => u.trim()).filter(Boolean);
    if (nonEmpty.length === 0) {
      setError('Please enter a URL');
      return;
    }

    const invalid = nonEmpty.find((u) => !validateUrl(u));
    if (invalid) {
      setError('Invalid URL format. Please enter valid media URLs.');
      return;
    }

    setError('');
    // Pass folder ID if selected, otherwise auto-assigned based on channel
    onSubmit(nonEmpty, selectedFolderId);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && urls.length === 1) {
      handleSubmit();
    }
  };

  const updateUrl = (index: number, value: string) => {
    const next = [...urls];
    next[index] = value;
    setUrls(next);
    setError('');
  };

  const addUrl = () => {
    setUrls([...urls, '']);
  };

  const removeUrl = (index: number) => {
    if (urls.length <= 1) return;
    setUrls(urls.filter((_, i) => i !== index));
  };

  const handleBlur = (url: string) => {
    if (url.trim() && validateUrl(url.trim()) && onUrlBlur) {
      onUrlBlur(url.trim());
    }
  };

  // --- File upload helpers ---

  const validateFiles = (files: File[]): File[] => {
    const valid: File[] = [];
    for (const file of files) {
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!ACCEPTED_TYPES.includes(ext) && !ACCEPTED_MIME.includes(file.type)) {
        setFileError(`Unsupported file type: ${file.name}. Accepted: ${ACCEPTED_TYPES.join(', ')}`);
        return [];
      }
      if (file.size > MAX_FILE_SIZE) {
        setFileError(`File too large: ${file.name} (${formatFileSize(file.size)}). Max 1 GB.`);
        return [];
      }
      valid.push(file);
    }
    return valid;
  };

  const handleFilesAdded = (newFiles: File[]) => {
    setFileError('');
    const validated = validateFiles(newFiles);
    if (validated.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validated]);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) handleFilesAdded(files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) handleFilesAdded(files);
    // Reset input so the same file can be re-selected
    e.target.value = '';
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => {
      const removed = prev[index];
      if (removed) {
        const key = fileKey(removed);
        setFileDurations((d) => {
          const next = { ...d };
          delete next[key];
          return next;
        });
      }
      return prev.filter((_, i) => i !== index);
    });
    setFileError('');
  };

  const handleFileSubmit = () => {
    if (selectedFiles.length === 0 || !onFilesSelected) return;
    // Pass selected folder ID if set
    onFilesSelected(selectedFiles, selectedFolderId, selectedLanguage);
    setSelectedFiles([]);
    setFileDurations({});
  };

  // Compute total estimated minutes from all URLs with known durations
  const totalEstimatedMinutes = urls.reduce((sum, url) => {
    const dur = previews?.[url.trim()]?.duration_minutes;
    return dur ? sum + dur : sum;
  }, 0);

  const hasEstimate = totalEstimatedMinutes > 0;
  const hasBalance = balance !== null && balance !== undefined;
  const insufficientBalance = hasEstimate && hasBalance && totalEstimatedMinutes > balance;

  return (
    <motion.div
      className={`w-full transition-all duration-500 ease-out ${
        collapsed ? 'py-6' : 'flex items-center justify-center'
      }`}
      style={{ height: collapsed ? 'auto' : 'auto', minHeight: collapsed ? undefined : '60vh' }}
      animate={{
        minHeight: collapsed ? 0 : '60vh',
      }}
    >
      <div className={`w-full ${collapsed ? 'max-w-4xl mx-auto' : 'max-w-2xl'}`}>
        <motion.div className="space-y-6" layout>
          {!collapsed && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="text-center space-y-3"
            >
              <FileAudio className="w-16 h-16 mx-auto text-[#0066FF]" strokeWidth={1.5} />
              <h1
                className="text-5xl font-bold tracking-tight"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                <span className="text-blue-600">Audio</span> Transcription
              </h1>
              <p className="text-lg text-[#666666] max-w-xl mx-auto">
                Fast, accurate transcription from any audio or video URL. Paste, upload or browse to get transcript in seconds.
              </p>
            </motion.div>
          )}

          {children}

          {!collapsed && (
            <div className="space-y-3">
              <label
                htmlFor="url-input-0"
                className="block text-lg font-semibold uppercase tracking-wide"
                style={{ fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '0.05em' }}
              >
                Media URL
              </label>

              {urls.map((url, i) => {
                const preview = previews?.[url.trim()];
                return (
                  <div key={i}>
                    <div className="flex gap-3">
                      {i === 0 ? (
                        <>
                          <motion.div
                            className="flex-1"
                            whileFocus={{ scale: 1.02 }}
                            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                          >
                            <Input
                              id="url-input-0"
                              name={`media-url-${Date.now()}`}
                              autoComplete="off"
                              type="url"
                              placeholder="https://example.com/audio.mp3"
                              value={url}
                              onChange={(e) => updateUrl(0, e.target.value)}
                              onKeyPress={handleKeyPress}
                              onBlur={() => handleBlur(url)}
                              className="h-14 px-6 text-base border-2 border-gray-300 focus:border-[#0066FF] focus:ring-2 focus:ring-[#0066FF]/20 transition-all duration-200"
                              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                            />
                          </motion.div>
                          
                          {/* Folder selector - between input and button */}
                          {isLoggedIn && (
                            <FolderSelector
                              selectedFolderId={selectedFolderId}
                              onSelectFolder={setSelectedFolderId}
                              compact
                            />
                          )}
                          
                          <motion.div
                            whileTap={{ scale: 0.96 }}
                            transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                          >
                            <Button
                              onClick={handleSubmit}
                              disabled={!urls.some((u) => u.trim())}
                              className="h-14 px-8 text-base font-semibold bg-[#0066FF] hover:bg-[#0052CC] text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                            >
                              {urls.length > 1 ? 'Transcribe All' : 'Transcribe'}
                            </Button>
                          </motion.div>
                        </>
                      ) : (
                        <>
                          <Input
                            name={`media-url-extra-${i}`}
                            autoComplete="off"
                            type="url"
                            placeholder="https://example.com/audio.mp3"
                            value={url}
                            onChange={(e) => updateUrl(i, e.target.value)}
                            onBlur={() => handleBlur(url)}
                            className="h-12 flex-1 px-4 text-sm border-2 border-gray-300 focus:border-[#0066FF] focus:ring-2 focus:ring-[#0066FF]/20 transition-all duration-200"
                            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                          />
                          <button
                            onClick={() => removeUrl(i)}
                            className="shrink-0 px-2 text-gray-400 hover:text-red-500 text-lg"
                            aria-label="Remove URL"
                          >
                            &times;
                          </button>
                        </>
                      )}
                    </div>
                    {preview && (
                      <p className="mt-1 text-xs text-gray-500 pl-1">
                        {preview.title && (
                          <>
                            Video: {preview.title}
                            {preview.author && ` by ${preview.author}`}
                          </>
                        )}
                        {preview.title && preview.duration_minutes && ' · '}
                        {preview.duration_minutes && (
                          <span className="text-gray-400">
                            Estimated: {preview.duration_minutes} min
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                );
              })}

              <button
                onClick={addUrl}
                className="text-sm font-medium text-blue-600 hover:text-blue-500"
              >
                + Add another URL
              </button>

              {/* Estimate summary below submit button */}
              {hasEstimate && (
                <p className={`text-xs ${insufficientBalance ? 'text-red-600' : 'text-gray-500'}`}>
                  Total: {totalEstimatedMinutes} min
                  {insufficientBalance ? (
                    <>
                      {' '}(you need {totalEstimatedMinutes - balance!} more min){' '}
                      <button type="button" onClick={onPurchaseMore} className="underline font-medium text-blue-600">Purchase more</button>
                    </>
                  ) : hasBalance ? (
                    <> ({balance! >= 99999 ? '∞' : balance! - totalEstimatedMinutes} min left after transcription)</>
                  ) : null}
                </p>
              )}

              {error && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-sm text-red-600 font-medium"
                >
                  {error}
                </motion.p>
              )}

              {!error && inlineError && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-sm text-red-600"
                >
                  {inlineError}
                </motion.p>
              )}

              {/* Divider */}
              <div className="flex items-center gap-4 pt-2">
                <div className="flex-1 border-t border-gray-200" />
                <span className="text-sm text-gray-400">or upload files</span>
                <div className="flex-1 border-t border-gray-200" />
              </div>

              {/* File upload zone */}
                <div className="space-y-3">
                  {/* Hidden file input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_TYPES.join(',')}
                    multiple
                    onChange={handleFileInputChange}
                    className="hidden"
                  />

                  {/* Drop zone */}
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 cursor-pointer transition-colors duration-200 ${
                      dragOver
                        ? 'border-[#0066FF] bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400 bg-gray-50/50'
                    }`}
                  >
                    <Upload className={`w-8 h-8 ${dragOver ? 'text-[#0066FF]' : 'text-gray-400'}`} strokeWidth={1.5} />
                    <p className="text-sm text-gray-500">
                      Drop audio/video files here or <span className="text-[#0066FF] font-medium">click to browse</span>
                    </p>
                    <p className="text-xs text-gray-400">
                      MP3, MP4, M4A, WAV, WebM — max 1 GB per file
                    </p>
                  </div>

                  {/* File list */}
                  {selectedFiles.length > 0 && (() => {
                    const totalFileMinutes = selectedFiles.reduce((sum, file) => {
                      const dur = fileDurations[fileKey(file)];
                      return dur ? sum + Math.ceil(dur / 60) : sum;
                    }, 0);
                    const allDurationsKnown = selectedFiles.every((f) => fileDurations[fileKey(f)] !== undefined);
                    const hasFileEstimate = totalFileMinutes > 0;
                    const insufficientFileBalance = hasFileEstimate && hasBalance && totalFileMinutes > balance!;

                    return (
                    <div className="space-y-2">
                      {selectedFiles.map((file, i) => {
                        const dur = fileDurations[fileKey(file)];
                        const estMin = dur ? Math.ceil(dur / 60) : null;
                        return (
                        <div
                          key={`${file.name}-${i}`}
                          className="flex items-center justify-between rounded-md border border-gray-200 bg-white px-3 py-2"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <FileAudio className="w-4 h-4 shrink-0 text-gray-400" strokeWidth={2} />
                            <span className="text-sm text-gray-800 truncate">{file.name}</span>
                            <span className="text-xs text-gray-400 shrink-0">
                              {formatFileSize(file.size)}
                              {estMin !== null && ` · Estimated: ${estMin} min`}
                            </span>
                          </div>
                          <button
                            onClick={() => removeFile(i)}
                            className="shrink-0 ml-2 text-gray-400 hover:text-red-500"
                            aria-label="Remove file"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        );
                      })}

                      {/* Total estimate for files */}
                      {hasFileEstimate && (
                        <p className={`text-xs ${insufficientFileBalance ? 'text-red-600' : 'text-gray-500'}`}>
                          Total: {totalFileMinutes} min{!allDurationsKnown && '+'}
                          {insufficientFileBalance ? (
                            <>
                              {' '}(you need {totalFileMinutes - balance!} more min){' '}
                              <button type="button" onClick={onPurchaseMore} className="underline font-medium text-blue-600">Purchase more</button>
                            </>
                          ) : hasBalance && isLoggedIn ? (
                            <> ({balance! >= 99999 ? '∞' : balance! - totalFileMinutes} min left after transcription)</>
                          ) : null}
                        </p>
                      )}

                      {/* Language selector for file uploads */}
                      <div className="flex items-center gap-2 mb-2">
                        <Globe className="w-4 h-4 text-gray-400" />
                        <label className="text-xs text-gray-500">Language:</label>
                        <select
                          value={selectedLanguage}
                          onChange={(e) => setSelectedLanguage(e.target.value)}
                          className="h-8 px-2 text-sm border border-gray-300 rounded bg-white focus:border-[#0066FF] focus:outline-none"
                          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                        >
                          {LANGUAGE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Folder selector for file uploads */}
                      {isLoggedIn && (
                        <div className="flex items-center gap-2 mb-2">
                          <label className="text-xs text-gray-500">Save to:</label>
                          <FolderSelector
                            selectedFolderId={selectedFolderId}
                            onSelectFolder={setSelectedFolderId}
                            compact
                          />
                        </div>
                      )}

                      <motion.div
                        whileTap={{ scale: 0.96 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                      >
                        <Button
                          onClick={handleFileSubmit}
                          className="h-12 w-full text-base font-semibold bg-[#0066FF] hover:bg-[#0052CC] text-white transition-colors duration-200"
                          style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                        >
                          {selectedFiles.length > 1 ? `Transcribe ${selectedFiles.length} Files` : 'Transcribe File'}
                        </Button>
                      </motion.div>
                    </div>
                    );
                  })()}

                  {fileError && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-sm text-red-600 font-medium"
                    >
                      {fileError}
                    </motion.p>
                  )}
                </div>

              {/* Browse podcasts divider */}
              <div className="flex items-center gap-4 pt-4">
                <div className="flex-1 border-t border-gray-200" />
                <span className="text-sm text-gray-400">or browse</span>
                <div className="flex-1 border-t border-gray-200" />
              </div>

              <p className="text-sm text-gray-500 text-center">
                Looking for a specific podcast?{' '}
                <a
                  href="/browse"
                  className="text-[#0066FF] font-medium hover:text-[#0052CC]"
                >
                  Browse our catalog
                </a>
              </p>
            </div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
}
